import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close menu on route change
    useEffect(() => { setOpen(false); }, [location]);

    // Prevent body scroll when menu is open + signal floating elements to hide
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        document.body.dataset.menuOpen = open ? "true" : "";
        return () => {
            document.body.style.overflow = "";
            delete document.body.dataset.menuOpen;
        };
    }, [open]);

    const solid = scrolled || !isHome;

    const links = [
        { href: "#states",    label: "Eight States",  isAnchor: true },
        { to: "/journeys",    label: "Journeys",      isAnchor: false },
        { href: "#festivals", label: "Festivals",     isAnchor: true },
        { href: "#manifesto", label: "Why We Travel", isAnchor: true },
    ];

    const goAnchor = (href) => (e) => {
        if (isHome) return;
        e.preventDefault();
        navigate(`/${href}`);
    };

    return (
        <>
            {/* ─── Top bar ─────────────────────────────────────────────────────────
                NOTE: backdrop-blur on this header creates a CSS containing block for
                position:fixed children — so the mobile overlay MUST live OUTSIDE this
                element (as a sibling in the Fragment) to cover the full viewport. */}
            <header
                data-testid="site-navbar"
                className={`fixed inset-x-0 top-0 z-[50] transition-all duration-500 ${
                    solid
                        ? "backdrop-blur-xl bg-paper/85 border-b border-forest/10"
                        : "bg-transparent"
                }`}
            >
                <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="logo-link"
                        className={`font-serif italic text-2xl tracking-tight ${
                            solid ? "text-forest" : "text-paper hero-shadow"
                        }`}
                    >
                        enei<span className="text-terracotta not-italic">.</span>in
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-10">
                        {links.map((l) =>
                            l.isAnchor ? (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={goAnchor(l.href)}
                                    data-testid={`nav-${l.href.replace("#", "")}`}
                                    className={`nav-link text-[13px] uppercase tracking-[0.18em] font-medium ${
                                        solid ? "text-forest" : "text-paper hero-shadow"
                                    }`}
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-${l.to.replace("/", "")}`}
                                    className={`nav-link text-[13px] uppercase tracking-[0.18em] font-medium ${
                                        solid ? "text-forest" : "text-paper hero-shadow"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            )
                        )}
                    </nav>

                    <Link
                        to="/journeys"
                        data-testid="nav-plan-trip-cta"
                        className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-[13px] uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5 ${
                            solid
                                ? "bg-forest text-paper hover:bg-terracotta"
                                : "bg-paper text-forest hover:bg-marigold hover:text-forest"
                        }`}
                    >
                        Plan my trip
                        <span aria-hidden>→</span>
                    </Link>

                    {/* Hamburger — z-[9995] keeps it above overlay (z-[9990]) */}
                    <button
                        data-testid="mobile-menu-toggle"
                        className={`md:hidden flex flex-col justify-center items-center w-10 h-10 gap-[6px] relative z-[9995] ${
                            solid || open ? "text-forest" : "text-paper"
                        }`}
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        aria-expanded={open}
                    >
                        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-300 origin-center ${open ? "rotate-45 translate-y-[7.5px]" : ""}`} />
                        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-300 ${open ? "opacity-0 scale-x-0" : ""}`} />
                        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-300 origin-center ${open ? "-rotate-45 -translate-y-[7.5px]" : ""}`} />
                    </button>
                </div>
            </header>

            {/* ─── Mobile full-screen overlay ──────────────────────────────────────
                Sibling to <header> so backdrop-blur cannot affect fixed positioning.
                z-[9990]: above floating WA button (9999→hidden via CSS) and scroll-top. */}
            <div
                data-testid="mobile-menu-overlay"
                className={`md:hidden fixed inset-0 z-[9990] bg-paper flex flex-col transition-all duration-300 ease-in-out ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
            >
                {/* Top bar inside overlay: logo + close button */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-forest/10">
                    <Link
                        to="/"
                        onClick={() => setOpen(false)}
                        className="font-serif italic text-2xl tracking-tight text-forest"
                    >
                        enei<span className="text-terracotta not-italic">.</span>in
                    </Link>
                    <button
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                        className="flex items-center justify-center w-10 h-10 text-forest hover:text-terracotta transition-colors"
                    >
                        {/* ✕ icon */}
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <line x1="3" y1="3" x2="17" y2="17" />
                            <line x1="17" y1="3" x2="3" y2="17" />
                        </svg>
                    </button>
                </div>

                {/* Nav links */}
                <div className="px-6 py-6 flex flex-col gap-1 overflow-y-auto flex-1">
                    {links.map((l) =>
                        l.isAnchor ? (
                            <a
                                key={l.href}
                                href={isHome ? l.href : `/${l.href}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mnav-${l.href.replace("#", "")}`}
                                className="text-forest text-2xl font-serif italic py-4 border-b border-forest/10 flex items-center justify-between"
                            >
                                {l.label}
                                <span className="text-terracotta text-base not-italic font-sans">→</span>
                            </a>
                        ) : (
                            <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mnav-${l.to.replace("/", "")}`}
                                className="text-forest text-2xl font-serif italic py-4 border-b border-forest/10 flex items-center justify-between"
                            >
                                {l.label}
                                <span className="text-terracotta text-base not-italic font-sans">→</span>
                            </Link>
                        )
                    )}
                    <Link
                        to="/journeys"
                        onClick={() => setOpen(false)}
                        data-testid="mnav-plan-trip-cta"
                        className="mt-8 inline-flex items-center justify-center bg-forest text-paper px-5 py-4 text-sm uppercase tracking-[0.18em]"
                    >
                        Plan my trip →
                    </Link>

                    {/* WhatsApp inside menu */}
                    <a
                        href="https://wa.me/917002345186"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center justify-center gap-3 border border-[#25D366] text-[#25D366] px-5 py-4 text-sm uppercase tracking-[0.18em]"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp us
                    </a>
                </div>
            </div>
        </>
    );
};
